import { apiClient } from '../lib/apiClient';
import type {
  IntakeChainStation,
  IntakeValues,
  PublisherIntakeSubmission,
  PublisherIntakeSummary,
  PublisherIntakeView,
} from '../types/publisherIntake';

const tokenBase = (token: string) =>
  `/publisher-intake/token/${encodeURIComponent(token)}`;

// The token in the path is the whole capability, so these are all anonymous.
const anon = { anonymous: true } as const;

export const publisherIntakeService = {
  lookup: (token: string) =>
    apiClient.get<PublisherIntakeView>(tokenBase(token), anon),

  // Drafts carry the form's own string-typed inputs; submit carries the
  // coerced payload from toIntakePayload. The server validates only the latter.
  saveDraft: (token: string, payload: Partial<IntakeValues>) =>
    apiClient.patch<{ ok: true; savedAt: string }>(tokenBase(token), { payload }, anon),

  /** Every station spawned from this link, for the sidebar rail. */
  chain: (token: string) =>
    apiClient.get<{ stations: IntakeChainStation[] }>(
      `${tokenBase(token)}/chain`,
      { anonymous: true },
    ),

  /** "Submit and Onboard Another Station": mint a sibling intake, same org. */
  continueIntake: (token: string) =>
    apiClient.post<{ token: string; url: string }>(
      `${tokenBase(token)}/continue`,
      {},
      { anonymous: true },
    ),

  submit: (token: string, payload: unknown) =>
    apiClient.post<{ ok: true; submittedAt: string }>(
      `${tokenBase(token)}/submit`,
      { payload },
      anon,
    ),

  clipPreviewUrl: (token: string, storagePath: string) =>
    apiClient.post<{ url: string; expiresIn: number }>(
      `${tokenBase(token)}/clip-preview-url`,
      { storagePath },
      anon,
    ),

  // ---- super admin ----

  listSubmissions: (status = 'submitted') =>
    apiClient.get<PublisherIntakeSummary[]>(
      `/publisher-intake?status=${encodeURIComponent(status)}`,
    ),

  getSubmission: (id: string) =>
    apiClient.get<PublisherIntakeSubmission>(`/publisher-intake/${encodeURIComponent(id)}`),

  createLink: (body: { organizationId: string; contactName?: string; contactEmail?: string }) =>
    apiClient.post<{ id: string; token: string; url: string; expiresAt: string }>(
      '/publisher-intake/links',
      body,
    ),

  revoke: (id: string) =>
    apiClient.post<{ ok: true }>(`/publisher-intake/${encodeURIComponent(id)}/revoke`),
};
